import CapacitySearchAction from './scheduling/capacity/CapacitySearchAction';
import MuleBadge from '../components/MuleBadge';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { customSearchLabel } from '../utils/customOrderSearch';
import { parseFilterJson, summarizeFilter } from './order-management/components/CustomFilterDialog';
import CustomFilterEditButton from '../components/CustomFilterEditButton';
import {
  TruckIcon,
  DocumentTextIcon,
  CubeIcon,
  TagIcon,
  UserIcon,
  ArchiveBoxIcon,
  MapIcon,
  MapPinIcon,
  WrenchScrewdriverIcon,
  ChevronRightIcon,
  IdentificationIcon,
  Squares2X2Icon,
  TableCellsIcon,
  PlusIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { TanTable } from '../components/TanTable';
import { deletedRowClass } from '../utils/deletedRow';
import { buildProBulkActions } from '../components/proBulkActions';
import {
  buildProCardColumns,
  PRO_CARD_HEADER_COLUMN_IDS,
  PRO_CARD_CORE_VISIBLE_IDS,
  PRO_CARD_COVERED_FIELDNAME_SET,
  applyRegistryTitles,
  buildProPickerFields,
  resolveFieldAliases,
  resolveRegistryTitles,
} from '../components/proCardColumns';
import searchService, { GlobalSearchResult, ProSearchData } from '../services/search.service';
import { mergeRefreshedProRows, isProRow } from '../utils/proRowRefresh';
import { TraceResultsProvider } from '../components/trace/TraceResultsContext';
import ProResultCard, { FieldHighlightProvider, PRO_CARD_FIELD_IDS } from '../components/ProResultCard';
import { ViewedProsProvider, useViewedPros } from '../contexts/ViewedProsContext';
import ProDetailSlideOver from '../components/pro/ProDetailSlideOver';
import TermChips from '../components/TermChips';
import QuoteLaneEditorDrawer from './order-management/components/QuoteLaneEditorDrawer';
import type { QuoteListRow } from './order-management/fixtures';
import { config } from '../config/config';
import ChangeTemplateBulkModal from './pro/components/ChangeTemplateBulkModal';
import { useCanEditOrderLcFields } from './pro/useCanEditOrderLcFields';
import { useCanUseChangeAll } from './pro/useCanUseChangeAll';
import { usePrefetchChangeAllFields } from '../hooks/useChangeAllFields';
import { useCanEditRailbillField, RAILBILL_FIELD_COMPONENT_ID } from './pro/useCanEditRailbillField';
import {
  useCanEditOrderWeightFields, ORDER_WEIGHT_FIELDS_COMPONENT_ID,
} from './pro/useCanEditOrderWeightFields';
import { useCanEditLegFields, LEG_EDITING_COMPONENT_ID } from './pro/useCanEditLegFields';
import { fetchOrderFieldDisplayRules, type OrderFieldDisplayRule } from '../services/orderFieldDisplay.service';
import { EditableCell } from '../components/gridEdit/EditableCell';
import { useStagedEdits } from '../components/gridEdit/useStagedEdits';
import {
  gridEditMetaFor, specFor, stagingKey, buildPayload, duplicatedPros, keysForPros,
  displayBlockedForRow, rowHasStagedEdit, keyPrefixForScope, INVOICE_RECNO_FIELD,
  INVOICE_EDITABLE_FIELDS, type GridFieldMap,
} from './searchResults/orderGridEdit';
import ordersService from '../services/orders.service';
import { useCanCreateOrder, ORDER_CREATE_COMPONENT_ID } from './pro/useCanCreateOrder';
import { useCreateOrderFlow } from './pro/createOrder/CreateOrderFlowContext';
import PermissionsContainer from '../components/PermissionsContainer';
import { useFieldDefinitions, FieldDefinition } from '../contexts/FieldDefinitionsContext';
import { generateColumnDef, getApiFieldName } from '../utils/dynamicColumns';
import userSettingsService from '../services/userSettings.service';
import { useSearchViews, resolveActiveView } from '../hooks/useSearchViews';
import { useProSearchDestination } from '../hooks/useProSearchDestination';
import { prepareTableTemplate } from '../utils/tableTemplateCompatibility';
import { entityOrdersType, entityOrdersTitle } from '../utils/entityOrdersSearch';
import SearchDocumentsView from './search-documents/SearchDocumentsView';
import SearchProgress from '../components/SearchProgress';
import { MAX_SEARCH_PROS } from '../types/documentsSearch';
import { enforceSingleExpandingBranch as enforceSingleBranch } from './searchResults/expandingBranch';

/** Mirrors CUSTOM_FILTER_TIMEOUT_SECONDS in app/api/api_v1/endpoints/search.py.
 *  Display only — the server enforces it; this just lets the wait state say when
 *  it will end. */
/** Mirrors CUSTOM_FILTER_TIMEOUT_SECONDS in app/api/api_v1/endpoints/search.py.
 *  Display only — the server enforces it and its rejection carries the real number;
 *  this just lets the wait state say when the wait ends. */
const CUSTOM_FILTER_BUDGET_SECONDS = 230;

// Persisted key for the search-results PRO table's user-chosen extra columns
// (invoice / invoice line-item / any orders field). TanTable does NOT persist
// additionalFields in its own saved settings, so the host owns this — mirrors
// the Quotes flat-grid pattern.
const PRO_ADDL_FIELDS_KEY = 'search-results-pro-additional-fields';


// Stable empty fallback — a fresh `[]` per render would churn TanTable's data.
const EMPTY_PRO_ROWS: ProSearchData[] = [];

// Display order for result groups; anything unlisted sorts last.
const GROUP_ORDER: Record<string, number> = {
  'Drivers': 1,
  'PRO Numbers': 2,
  'Locations': 3,
  'Containers': 4,
  'Chassis': 5,
  'BOLs': 6,
  'Customers': 7,
  'Dispatchers': 8,
  'Orders by Driver': 9,
};

// Templates ("T110") reuse the PRO payload builder on the backend, so their
// `.data` is the same rich ProSearchData shape. Treat them like PRO results
// for the grid/table + row-click-opens-drawer path (icon/label still differ
// by type, keeping the "Template" distinction visible).
const isProLike = (type: GlobalSearchResult['type']) => type === 'pro' || type === 'template';

/**
 * The fields a TEMPLATE card shows, in order — a deliberately short set, because a
 * template is a blueprint and most order columns are empty on one. Measured over
 * the 14,073 live prod templates: container / chassis / vessel / voyage / PO / BOL /
 * pickup# are populated on ZERO of them, Terminal on 2, and every date column is a
 * dispatch artifact a template has no business carrying. Rendering the full order
 * grid for a template therefore showed ~40 columns of em-dashes.
 *
 * What survives is what templates actually carry: parties (Bill To / Customer 100%,
 * CSR 100%, Dispatcher 93%, Whse CSR 23%), the lane (Deliver To 99%, Pickup From
 * 56%), and the equipment defaults (Commodity 33%, Type 18%, Size 12%). Ten fields
 * = exactly one card row (FIELDS_PER_ROW).
 *
 * Pro #, the Deleted badge, Move Type, Master Pro and DROP are NOT listed: the card
 * header already renders them.
 */
const TEMPLATE_CARD_FIELDS: { id: string; label: string }[] = [
  { id: 'billToName', label: 'Bill To' },
  { id: 'customerName', label: 'Customer' },
  { id: 'csr', label: 'CSR' },
  { id: 'whseCsr', label: 'Whse CSR' },
  { id: 'dispatcher', label: 'Dispatcher' },
  { id: 'pickupFrom', label: 'Pickup From' },
  { id: 'deliverTo', label: 'Deliver To' },
  { id: 'commodity', label: 'Commodity' },
  { id: 'size', label: 'Size' },
  { id: 'equipType', label: 'Type' },
];

const SearchResultsContent: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Green "viewed" highlight for PROs opened during this session (mirrors the
  // At Risk Monitor). In-memory via context, shared by both the Single Row View
  // table cell and the Multi Row View card.
  const { markViewed } = useViewedPros();

  // Pre-fills the Documents view's bulk-email recipient. `account.username` is
  // the MSAL UPN — the same value PermissionContext sends as `user_email` — so
  // it matches what the rest of the app calls "the current user". Read here
  // rather than inside SearchDocumentsView so that view needs no MSAL provider.
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username || '';

  // Get query params
  const query = searchParams.get('q') || '';
  const openPro = searchParams.get('pro') || null;
  const openQuote = searchParams.get('quote') || null;
  const isMulti = searchParams.get('multi') === 'true';
  const isClosed = searchParams.get('closed') === 'true';
  // Entity-scoped view: every order for one driver / customer / dispatcher,
  // reached by clicking an aggregate row in the top-bar dropdown (or the
  // matching group on this page). `q` holds that entity's exact identity.
  const ordersBy = searchParams.get('ordersBy');
  const ordersLabel = searchParams.get('label');
  const entityType = entityOrdersType(ordersBy);
  // Custom Search (header magnifier menu): match a pasted list against ONE field.
  // `q` is newline-joined here so LOT NUMBER values keep their commas/spaces.
  const searchBy = searchParams.get('searchBy');
  // Custom Filter: a JSON FilterCondition[] instead of search terms. There's no
  // `q` in this mode, so the "needs a query" guard below has to allow it through.
  const filterJson = searchParams.get('filter');
  const customIncludeDeleted = searchParams.get('includeDeleted') === 'true';
  const customTerms = searchBy ? query.split(/[\r\n]+/).map((t) => t.trim()).filter(Boolean) : [];
  // A manager-published View chosen in the Custom Search / Custom Filter modal.
  // The id rides the URL so the layout is part of the shareable, reload-safe
  // results view — and so dropping it is just a param removal.
  const viewIdParam = searchParams.get('view') || '';
  // A plain free-text search — the only mode whose scope is switchable in place.
  // Custom Filter / Custom Search are permanently all-scope with their own
  // includeDeleted checkbox, an entity view is scoped to one driver/customer/
  // dispatcher, and a batch paste already looks up every term in ordersall.
  const isPlainSearch = !filterJson && !searchBy && !entityType && !isMulti;

  // Search state
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Card vs. Table view. Persisted so the choice sticks across searches/sessions.
  const [viewMode, setViewMode] = useState<'cards' | 'table'>(
    () => (localStorage.getItem('search-results-view-mode') as 'cards' | 'table') || 'cards'
  );

  // Published Views for this grid, both modes merged. `viewsLoading` gates the
  // first fetch below: resolving the URL's view id has to happen BEFORE the
  // search runs, because a View can carry extra columns the backend must be
  // asked for. Fetch only when a view is actually requested — an ordinary search
  // should not pay for a lookup it will not use.
  // `all`, not just the published Views: the picker also offers the user's own
  // templates under "My Views", and a `?view=<id>` can name either kind (both are
  // rows in table_layout_templates, so the id is unambiguous).
  const { all: selectableViews, loading: viewsLoading } = useSearchViews(!!viewIdParam);
  // resolveActiveView owns the fallbacks (deleted row, corrupt snapshot, still
  // loading) — see its docstring. A View belongs to the mode it was saved from, so
  // `mode` takes the page there; without one it is the user's own choice.
  // The grid as a layout DESTINATION, neutral of whatever extra columns are on
  // screen right now — the same object the picker filters against, so a View it
  // offered can never be refused here. A View from another grid names its columns
  // as that grid spelled them, and `initialSettings` is applied BY ID, so the
  // snapshot has to be fitted before the table ever sees it.
  const proSearchDestination = useProSearchDestination();
  const fitViewSnapshot = useCallback(
    (snapshot: any) => prepareTableTemplate(snapshot, proSearchDestination),
    [proSearchDestination],
  );
  const resolvedView = useMemo(
    () => resolveActiveView(
      selectableViews, viewIdParam, viewsLoading, viewMode,
      proSearchDestination.ready ? fitViewSnapshot : undefined,
    ),
    [selectableViews, viewIdParam, viewsLoading, viewMode, proSearchDestination.ready, fitViewSnapshot],
  );
  const activeView = resolvedView.view;
  const viewActive = resolvedView.active;
  const viewUnresolved = resolvedView.unresolved;
  const viewSnapshot = resolvedView.snapshot;
  const effectiveViewMode = resolvedView.mode;

  useEffect(() => {
    // Only the user's OWN choice is remembered. A View forcing the page into its
    // mode is a property of that View, not a preference — persisting it would
    // leave the user in an unexpected mode on their next ordinary search.
    localStorage.setItem('search-results-view-mode', viewMode);
  }, [viewMode]);

  // Drop the View, keeping the search itself. Used by the header's ✕ and by
  // edit-mode Save (which has just adopted the layout as the user's own).
  const clearActiveView = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  // The mode toggle. A View owns the mode it was saved in, so asking for the
  // OTHER mode means leaving the View — keeping both would show the View's name
  // over a layout that is not the View's.
  const selectViewMode = useCallback(
    (mode: 'cards' | 'table') => {
      setViewMode(mode);
      if (viewActive && activeView!.mode !== mode) clearActiveView();
    },
    [activeView, clearActiveView, viewActive],
  );

  // Documents view: every document attached to the PROs this search returned,
  // rendered INSTEAD of the orders grid (V1 searchdisplay=documents parity).
  // Deliberately not persisted — it's a per-search detour, not a preference.
  const [docsView, setDocsView] = useState(false);

  // Change All / Change Template for All. Both views render the same PRO
  // TanTable (cards via renderCardRow), so these live in the table's action
  // bar in BOTH modes, scoped to that table's own visible/filtered rows
  // (read straight from the table at click time).
  const canEditOrderLcFields = useCanEditOrderLcFields();
  // Change All has its own component since the split — Dispatch/Scheduling hold it
  // for trouble-ticket fan-out without holding the LC field gate. Change Template
  // for All stays on the LC gate above (it rewrites order fields wholesale).
  const canUseChangeAll = useCanUseChangeAll();
  // Warm the Change All field registry now rather than when the first
  // checkbox mounts, so the drawer's first paint is already the registry's
  // answer and not the built-in fallback. No request for users without the
  // grant — see usePrefetchChangeAllFields.
  usePrefetchChangeAllFields(canUseChangeAll);
  // "+ Create New Order" header button — gated by the same component the
  // create flow's other entry points use (design doc §10).
  const canCreateOrder = useCanCreateOrder();
  const { openCreateOrder } = useCreateOrderFlow();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPros, setBulkPros] = useState<(number | string)[]>([]);
  // Change All — opens the order drawer in change-all mode on the first filtered
  // pro (the anchor); flagged fields fan out to the rest of these pros on Save.
  const [changeAllPros, setChangeAllPros] = useState<(number | string)[] | null>(null);
  // Bumped after a bulk apply to re-run the search so the grid reflects the changes.
  const [refetchNonce, setRefetchNonce] = useState(0);

  // User-chosen extra PRO columns (invoice / invoice line-item / any displayable
  // orders field — the same universe as the custom-filter buckets). Host-owned +
  // persisted (TanTable doesn't save additionalFields itself). The search refetch
  // sends these so the backend returns each field's value on the row.
  const [additionalFields, setAdditionalFields] = useState<string[]>([]);
  // Gate the first fetch until the persisted field list is loaded, so the initial
  // search already includes the saved columns' data (no flash / no double fetch).
  const [fieldsLoaded, setFieldsLoaded] = useState(false);
  const { fieldDefinitions, loading: fieldDefsLoading } = useFieldDefinitions();
  // Transient note when a picked column was auto-dropped to avoid duplicating rows.
  const [columnNote, setColumnNote] = useState<string | null>(null);

  // Restore the saved column selection, reconciled against the table's
  // saved column visibility. A requested extra field that the user has HIDDEN must
  // not stay requested — it would still be fetched and (for a line-item field)
  // fan the pro into multiple rows with the column invisible. So drop any saved
  // field whose column is saved-hidden. (Removing an extra column via Show/Hide
  // already drops it from the request; this heals older saved state + any
  // hide done through a non-picker path.)
  useEffect(() => {
    // A View carries its own additionalFields (buildTemplateSnapshot captures
    // them), and its layout is on loan — so the user's saved selection is neither
    // restored nor reconciled here. Gate on viewsLoading too: restoring the
    // user's fields first and swapping to the View's after would fire a second
    // search with a different column set.
    if (viewIdParam && viewsLoading) return;
    if (viewActive) {
      setAdditionalFields(resolvedView.additionalFields);
      setFieldsLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      let saved: string[] = [];
      try {
        const s = await userSettingsService.getUserSetting(PRO_ADDL_FIELDS_KEY);
        // Stored as { fields: string[] } — the user-settings API requires an
        // object body (a bare array is rejected).
        const fields = s?.setting_value?.fields;
        if (Array.isArray(fields)) saved = fields as string[];
      } catch { /* non-fatal — fall back to base columns */ }
      const hidden = new Set<string>();
      try {
        const t = await userSettingsService.getUserSetting('table_search-results-pro-table');
        const vis = t?.setting_value?.columnVisibility as Record<string, boolean> | undefined;
        if (vis && typeof vis === 'object') {
          for (const [k, v] of Object.entries(vis)) if (v === false) hidden.add(k);
        }
      } catch { /* non-fatal — keep all saved fields */ }
      const cleaned = saved.filter((f) => !hidden.has(f));
      if (cancelled) return;
      if (cleaned.length) setAdditionalFields(cleaned);
      if (cleaned.length !== saved.length) {
        void userSettingsService.saveUserSetting(PRO_ADDL_FIELDS_KEY, { fields: cleaned });
      }
      setFieldsLoaded(true);
    })();
    return () => { cancelled = true; };
    // Re-runs when the active View changes, not only on mount: entering a View
    // swaps in its field set, and leaving one restores the user's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewIdParam, viewsLoading, viewActive, resolvedView]);

  // defByName turns a selected fieldname back into a generated column and
  // resolves each field's row-expanding branch. NOTE: the generated column's
  // accessorKey is getApiFieldName(fieldname), NOT the raw db fieldname — a
  // handful of names are remapped (dispatchername -> dispatcher_name,
  // revenueregion -> region, ...), so anything comparing against column ids has
  // to map through getApiFieldName too. (availableProFields — the picker
  // universe — is built after proColumns so it can include the static columns.)
  // Only the Custom Filter fans the generic `leg` join (see the enrichment call in
  // search.py): it is the one surface with a WHERE to prune the fan down to the legs
  // that MATCHED, so a leg column there means "one row per matching leg". On Custom
  // Search or an entity view the same column is a single collapsed leg, so the
  // duplicate-row machinery below must stay off or it would warn about a fan that
  // never happens.
  const legFanActive = !!filterJson;

  /** Does displaying this field duplicate the pro row, on THIS surface? `expands` is
   *  unconditional; `expands_leg_fan` only counts where legs are actually fanning. */
  const fieldExpands = useCallback(
    (d?: FieldDefinition) => !!(d && (d.expands || (legFanActive && d.expands_leg_fan))),
    [legFanActive],
  );

  const defByName = useMemo(
    () => new Map(fieldDefinitions.map((d) => [d.fieldname, d])),
    [fieldDefinitions],
  );

  // Human-readable "Field is Value" lines for the Custom Filter header/tooltip.
  const filterSummary = useMemo(
    () => (filterJson ? summarizeFilter(filterJson, fieldDefinitions) : []),
    [filterJson, fieldDefinitions],
  );

  // Re-run the search against an edited filter. Written to the URL so the refined
  // view is still shareable and reload-safe. `pro` is deliberately NOT carried
  // over: the order whose drawer was open may not match the new conditions.
  const handleFilterEdited = useCallback((
    nextFilter: string,
    nextIncludeDeleted: boolean,
    nextViewId: string,
  ) => {
    const params: Record<string, string> = { filter: nextFilter };
    if (nextIncludeDeleted) params.includeDeleted = 'true';
    // Whatever the reopened modal came back with — the same View, a different
    // one, or none. Rebuilding the params without it would silently drop the
    // layout the user is looking at.
    if (nextViewId) params.view = nextViewId;
    setSearchParams(params);
  }, [setSearchParams]);

  // At most ONE row-expanding branch, so two one-to-many joins can't Cartesian-product
  // the grid. The branch comes from the server per field — see ./searchResults/expandingBranch.
  const enforceSingleExpandingBranch = useCallback(
    (fields: string[]) => enforceSingleBranch(fields, defByName, fieldExpands),
    [defByName, fieldExpands],
  );

  // Add/remove an extra column: enforce one expanding branch, update state (which
  // re-runs the search via the fetch effect below), and persist the choice.
  const handleAdditionalFieldsChange = useCallback((next: string[]) => {
    const { kept, dropped } = enforceSingleExpandingBranch(next);
    setColumnNote(
      dropped.length
        ? `Line-item columns must come from one invoice group. Kept your current group and skipped: ${dropped
            .map((fn) => defByName.get(fn)?.displayname || fn).join(', ')}. Remove those columns first to switch groups.`
        : null,
    );
    setAdditionalFields(kept);
    // Under an active View the column set is on loan with the rest of the layout,
    // so a change here drives the refetch but does NOT rewrite the user's own
    // saved selection — dropping the View must bring their columns back.
    if (!viewActive) {
      void userSettingsService.saveUserSetting(PRO_ADDL_FIELDS_KEY, { fields: kept });
    }
  }, [enforceSingleExpandingBranch, defByName, viewActive]);

  // Heal a previously-saved selection that mixes expanding branches (from before
  // this guard existed). Runs once, after the field registry has loaded so the
  // branch of each field is known.
  const healedRef = useRef(false);
  // Gates the first search until healing has had its say. Without it the sequence is
  // "search with the saved columns" -> heal trims them -> "search again with fewer",
  // i.e. two queries per page load. The first is abandoned client-side but the SERVER
  // runs it to completion (a blocking call in the threadpool has no cancellation
  // point), so every load cost two full filter scans. Same class of bug the viewsLoading
  // gate above already fixed for Views.
  const [fieldsSettled, setFieldsSettled] = useState(false);
  useEffect(() => {
    if (healedRef.current || !fieldsLoaded) return;
    // Wait for the registry, but on its loading flag rather than on a non-empty list:
    // if it failed to load there is nothing to heal against, and blocking search
    // forever would be worse than skipping the heal. The context always clears the
    // flag in a finally, so this cannot hang.
    if (fieldDefsLoading) return;
    healedRef.current = true;
    // Never heal a View's field set: it isn't the user's to repair, and the write
    // below would push the View's columns into their own saved selection. Their
    // own set gets healed the next time they load the page without a View.
    if (viewActive) {
      setFieldsSettled(true);
      return;
    }
    // Drop anything a built-in column already shows. The picker no longer offers
    // these, but a selection saved BEFORE that suppression still carries them —
    // and would render a second column with the same values under a different
    // label (the "Last Free Day" / LFD pair being the obvious one).
    const covered = additionalFields.filter((fn) => PRO_CARD_COVERED_FIELDNAME_SET.has(fn));
    const uncovered = additionalFields.filter((fn) => !PRO_CARD_COVERED_FIELDNAME_SET.has(fn));
    const { kept, dropped } = enforceSingleExpandingBranch(uncovered);
    if (dropped.length || covered.length) {
      setAdditionalFields(kept);
      void userSettingsService.saveUserSetting(PRO_ADDL_FIELDS_KEY, { fields: kept });
      const name = (fn: string) => defByName.get(fn)?.displayname || fn;
      setColumnNote([
        dropped.length
          ? `Removed ${dropped.length} saved column(s) from a different invoice group that were duplicating rows: ${dropped.map(name).join(', ')}.`
          : '',
        covered.length
          ? `Removed ${covered.length} saved column(s) already shown by a built-in column: ${covered.map(name).join(', ')}.`
          : '',
      ].filter(Boolean).join(' '));
    }
    setFieldsSettled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldsLoaded, fieldDefinitions, fieldDefsLoading, viewActive]);

  // Track which groups are collapsed (default: all expanded)
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = useCallback((name: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  // Order drawer pronumber, derived from the URL. ProDetailSlideOver fetches
  // and refreshes the order itself, so we only need the pronumber + open flag.
  const selectedPro = useMemo<number | string | null>(() => {
    if (!openPro) return null;
    // Templates carry a "T"-prefixed pronumber (e.g. "T110"); keep the raw
    // string so parseInt doesn't mangle it. Numeric pros are parsed exactly as
    // before, so their drawer path is byte-for-byte unchanged.
    if (/^T/i.test(openPro)) return openPro;
    const n = parseInt(openPro, 10);
    return Number.isFinite(n) ? n : null;
  }, [openPro]);

  const legacyCmsUrl = config.LEGACY_CMS_URL;

  // Parse multi terms for display
  const multiTerms = isMulti ? query.split(',').map(t => t.trim()).filter(Boolean) : [];

  // Distinct terms with occurrence counts, in numeric-aware order. A repeated
  // term (e.g. a pro late on multiple days in a metrics link) gets one chip
  // with a ×N badge — the results below only ever show it once.
  const termChips: [string, number][] = (() => {
    const counts = new Map<string, number>();
    for (const t of multiTerms) counts.set(t, (counts.get(t) || 0) + 1);
    return Array.from(counts.entries()).sort(([a], [b]) =>
      a.localeCompare(b, undefined, { numeric: true })
    );
  })();

  // User-picked extra columns are requested in BOTH views: Multi-Row (cards) now
  // renders through the same TanTable (one card per row), so the card surfaces
  // them as an "Additional" band and needs their values on the row. A
  // row-expanding field fans cards exactly like it fans table rows — coherent,
  // and already guarded to a single branch by enforceSingleExpandingBranch.
  // Joined into a STRING key on purpose: both handleAdditionalFieldsChange and
  // the healing effect below build fresh arrays, so depending on the array
  // itself would refetch on every same-content identity change. Don't inline.
  const additionalFieldsKey = additionalFields.join(',');

  // Fetch search results
  useEffect(() => {
    // Wait for the persisted extra-column list AND for the healing pass, so the first
    // fetch already carries the final column set. Either one landing later would fire a
    // second search — and abandon the first one server-side, still running.
    if (!fieldsLoaded || !fieldsSettled) return;

    const sendFields = additionalFieldsKey ? additionalFieldsKey.split(',') : [];
    // An accessorial-invoice edit is addressed by the invoice's own recno, so the row
    // has to carry it. Requested alongside — but NOT added to additionalFields, so it
    // never becomes a column the user has to see, reorder or wonder about. Without
    // this, adding "Any Invoice - Invoice Comments" produced cells that looked
    // editable-shaped but weren't, with nothing on screen saying why.
    if (sendFields.some((f) => INVOICE_EDITABLE_FIELDS.has(f))
        && !sendFields.includes(INVOICE_RECNO_FIELD)) {
      sendFields.push(INVOICE_RECNO_FIELD);
    }

    // Abort the previous search when the inputs change or the page unmounts.
    //
    // This frees the CLIENT — it does not stop the query. A sync endpoint's blocking DB
    // call has no cancellation point, so the server runs to completion regardless;
    // that is what CUSTOM_FILTER_TIMEOUT_SECONDS bounds. What this does fix is a
    // navigate-away leaving the request pending forever, and a slow first search
    // landing on top of a fast second one.
    const abort = new AbortController();

    const fetchResults = async () => {
      // Custom Filter carries its criteria in `filter`, not `q`.
      if (!filterJson && (!query || query.trim().length < 1)) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        let apiResults;
        if (filterJson) {
          // Custom Filter — AND-ed field/operator/value conditions over orders.
          // Uncapped by design (reporting/export surface), so a broad filter can be
          // slow and large; there is no truncation to report.
          apiResults = await searchService.customFilterSearch(
            parseFilterJson(filterJson).map(({ field, compare, values }) => ({ field, compare, values })),
            customIncludeDeleted, abort.signal, sendFields,
          );
        } else if (searchBy) {
          // Custom Search — one field, many pasted values. Scope is open + closed
          // orders; the toggle only controls whether deleted rows come back.
          apiResults = await searchService.customSearch(
            searchBy, customTerms, customIncludeDeleted, abort.signal, sendFields,
          );
        } else if (entityType) {
          // Every order for ONE driver / customer / dispatcher, as PRO rows.
          // `types` opts into the entity sub-search, which the plain typeahead
          // pass never runs; scope stays 'current' so the grid matches the order
          // count the aggregate row advertised.
          apiResults = await searchService.unifiedGlobalSearch(
            query, 500, abort.signal, 'current', entityType, false, sendFields,
          );
        } else if (isMulti) {
          // Batch search for multiple terms
          const terms = query.split(',').map(t => t.trim()).filter(Boolean);
          apiResults = await searchService.batchSearch(terms, abort.signal, sendFields);
        } else {
          // The "closed" larger search also includes DELETED records (red-highlighted) —
          // scope='all' reaches the *all tables where deleted orders live, and
          // include_deleted relaxes the deleted='N' filter.
          // Request the endpoint's per-category ceiling (not the typeahead's ~5) so a
          // booking-number search returns every matching order as a full grid (V1 parity).
          // ordersAsPros: a container or chassis number identifies an ORDER, so
          // this page wants those matches as PRO rows in the grid — a container
          // carried by two orders was rendering as a thin "Containers (2)" list.
          // The typeahead leaves the flag off and keeps its identifier rows.
          apiResults = await searchService.unifiedGlobalSearch(
            query, 500, abort.signal, isClosed ? 'all' : 'current', undefined, isClosed, sendFields, true,
          );
        }
        if (abort.signal.aborted) return;
        setResults(apiResults);
      } catch (err: any) {
        // A superseded or abandoned search is not an error — reporting one would flash
        // a failure message over results the user is already navigating away from.
        if (abort.signal.aborted || err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
        // Prefer the server's own wording: the Custom Filter's timeout response
        // explains the budget and names the fix, which a generic message would discard.
        const detail = err?.response?.data?.detail;
        setError(typeof detail === 'string' && detail
          ? detail
          : 'Failed to fetch search results');
        setResults([]);
      } finally {
        if (!abort.signal.aborted) setIsLoading(false);
      }
    };

    fetchResults();
    return () => abort.abort();
  }, [query, isMulti, isClosed, entityType, searchBy, filterJson, customIncludeDeleted, refetchNonce, fieldsLoaded, fieldsSettled, additionalFieldsKey]);

  // Handle PRO click - preserve multi/closed flags if present
  const handleProClick = useCallback((proNumber: string) => {
    markViewed(proNumber);
    const params: Record<string, string> = { q: query, pro: proNumber };
    if (isMulti) params.multi = 'true';
    if (isClosed) params.closed = 'true';
    // Keep the entity scope, or closing the drawer would drop back to a plain
    // search for the raw identity (e.g. "008") instead of that driver's orders.
    if (ordersBy) params.ordersBy = ordersBy;
    if (ordersLabel) params.label = ordersLabel;
    // Same for Custom Search, or closing the drawer would re-run `q` as a plain
    // free-text search across every facet instead of the one field chosen.
    if (searchBy) params.searchBy = searchBy;
    if (filterJson) params.filter = filterJson;
    if (customIncludeDeleted) params.includeDeleted = 'true';
    setSearchParams(params);
  }, [query, isMulti, isClosed, ordersBy, ordersLabel, searchBy, filterJson, customIncludeDeleted, setSearchParams, markViewed]);

  /**
   * Widen (or re-narrow) a plain search to closed + deleted records, in place.
   *
   * This is the ONLY way to reach a deleted order or template from a query that
   * matched something: the typeahead's "Search Closed & Deleted" link renders only
   * in its NO-RESULTS branch, so a query like "81407" — which matches quote 81407-1
   * and therefore has results — offered no route to deleted template T81407 at all.
   * "View all results" doesn't help either: it re-runs the same current-scope
   * search, just uncapped.
   *
   * Drops `pro` so the drawer doesn't stay open on a row the widened search may not
   * return; everything else in the URL is preserved.
   */
  const setClosedScope = useCallback((on: boolean) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (on) next.set('closed', 'true');
      else next.delete('closed');
      next.delete('pro');
      return next;
    });
  }, [setSearchParams]);

  // Handle drawer close
  const handleCloseDrawer = useCallback(() => {
    setSearchParams(prev => {
      prev.delete('pro');
      return prev;
    });
  }, [setSearchParams]);

  // Change All / Change Template for All — openers behind the PRO table's
  // action bar (which serves both view modes).
  // Templates (T-prefixed) are never editable/re-templatable, so drop them.
  const openChangeTemplateFor = useCallback((pros: (number | string)[]) => {
    const filtered = pros.map(String).filter((p) => p && !p.toUpperCase().startsWith('T'));
    if (filtered.length === 0) return;
    setBulkPros(filtered);
    setBulkOpen(true);
  }, []);
  const openChangeAllFor = useCallback((pros: (number | string)[]) => {
    const filtered = pros.map(String).filter((p) => p && !p.toUpperCase().startsWith('T'));
    if (filtered.length === 0) return;
    // Change All and the single-pro (?pro) drawer are mutually exclusive.
    if (openPro) handleCloseDrawer();
    setChangeAllPros(filtered);
  }, [openPro, handleCloseDrawer]);
  // Mass actions (both views) live in the PRO table's action bar, scoped to
  // that table's visible/filtered rows.
  const proBulkActions = useMemo(
    () => buildProBulkActions({
      canChangeAll: canUseChangeAll,
      canChangeTemplate: canEditOrderLcFields,
      onChangeAll: openChangeAllFor,
      onChangeTemplateAll: openChangeTemplateFor,
      extra: [
        { key: 'capacity-request', render: (getRows) => <CapacitySearchAction getRows={getRows} /> },
        { key: 'capacity-link', render: (getRows) => <CapacitySearchAction getRows={getRows} link /> },
      ],
    }),
    [canUseChangeAll, canEditOrderLcFields, openChangeAllFor, openChangeTemplateFor],
  );

  // Quote editor drawer (mirrors the PRO drawer). The `quote` param is a quoteId
  // "number-rev"; build the minimal lane the drawer needs to load the full quote.
  const selectedQuoteLane = useMemo<QuoteListRow | null>(() => {
    if (!openQuote) return null;
    const [numStr, revStr] = openQuote.split('-');
    const quoteNumber = parseInt(numStr, 10);
    const revisionNumber = parseInt(revStr || '1', 10);
    if (!Number.isFinite(quoteNumber)) return null;
    return {
      quoteId: openQuote,
      quoteNumber,
      revisionNumber: Number.isFinite(revisionNumber) ? revisionNumber : 1,
      quoteType: 'DIRECT',
      customer: '', billTo: '', lane: '', csr: '',
      effective: '', expires: '', pros: 0, status: 'UNSECURED', hot: false,
    } as QuoteListRow;
  }, [openQuote]);

  const handleQuoteClick = useCallback((quoteId: string) => {
    const params: Record<string, string> = { q: query, quote: quoteId };
    if (isMulti) params.multi = 'true';
    if (isClosed) params.closed = 'true';
    if (ordersBy) params.ordersBy = ordersBy;
    if (ordersLabel) params.label = ordersLabel;
    setSearchParams(params);
  }, [query, isMulti, isClosed, ordersBy, ordersLabel, setSearchParams]);

  const handleCloseQuoteDrawer = useCallback(() => {
    setSearchParams(prev => {
      prev.delete('quote');
      return prev;
    });
  }, [setSearchParams]);

  // Handle driver navigation
  const handleDriverMapClick = (unitNumber: string) => {
    navigate(`/fleet-management/live-map?driver=${unitNumber}`);
  };

  const handleDriverDSMClick = (unitNumber: string) => {
    navigate(`/fleet-management/driver-status-monitor?driver=${unitNumber}`);
  };

  // Handle location click - navigate to Locations page
  const handleLocationClick = () => {
    navigate('/data-management/locations');
  };

  // Handle container click - open Order Detail Drawer via associated PRO
  const handleContainerClick = useCallback((result: GlobalSearchResult) => {
    const proPro = result.data?.pronumber;
    if (proPro) {
      handleProClick(String(proPro));
    }
  }, [handleProClick]);

  // Handle chassis click - open Order Detail Drawer via associated PRO
  const handleChassisClick = useCallback((result: GlobalSearchResult) => {
    const proPro = result.data?.pronumber;
    if (proPro) {
      handleProClick(String(proPro));
    }
  }, [handleProClick]);

  // Handle BOL click - open Order Detail Drawer via associated PRO
  const handleBolClick = useCallback((result: GlobalSearchResult) => {
    const proPro = result.data?.pronumber;
    if (proPro) {
      handleProClick(String(proPro));
    }
  }, [handleProClick]);

  // Aggregate rows on this page (Orders by Driver / Customers / Dispatchers) —
  // same click-through as the top-bar dropdown: reload this page scoped to that
  // entity so its orders render as PRO rows in the grid.
  const handleEntityOrdersClick = useCallback((result: GlobalSearchResult) => {
    const kind = result.type === 'driver_orders' ? 'driver' : result.type;
    navigate(
      `/searchresults?q=${encodeURIComponent(result.value)}`
      + `&ordersBy=${kind}`
      + `&label=${encodeURIComponent(result.label)}`,
    );
  }, [navigate]);

  // Handle legacy CMS clicks
  const handleLegacyClick = (result: GlobalSearchResult) => {
    let url = '';

    switch (result.type) {
      // Move Types only — it's far too broad for the grid (and filtered off
      // this page entirely). Driver/customer/dispatcher now stay in-app.
      case 'movetype':
        const proNumbers = result.data?.pronumbers?.slice(0, 50).join(',') || '';
        if (proNumbers) {
          url = `${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${proNumbers}&searchtype=pronumber`;
          window.open(url, '_blank', 'noopener,noreferrer');
        }
        break;
    }
  };

  // Get icon for result type
  const getIconForType = (type: GlobalSearchResult['type']) => {
    switch (type) {
      case 'pro': return CubeIcon;
      case 'driver': return TruckIcon;
      case 'customer': return DocumentTextIcon;
      case 'dispatcher': return UserIcon;
      case 'driver_orders': return TruckIcon;
      case 'movetype': return TagIcon;
      case 'container': return ArchiveBoxIcon;
      case 'chassis': return WrenchScrewdriverIcon;
      case 'bol': return IdentificationIcon;
      case 'location': return MapPinIcon;
      default: return DocumentTextIcon;
    }
  };

  // Get color for result type
  const getTypeColor = (type: GlobalSearchResult['type']) => {
    switch (type) {
      case 'pro': return 'text-purple-600 dark:text-purple-400';
      case 'driver': return 'text-green-600 dark:text-green-400';
      case 'customer': return 'text-blue-600 dark:text-blue-400';
      case 'dispatcher': return 'text-orange-600 dark:text-orange-400';
      case 'driver_orders': return 'text-teal-600 dark:text-teal-400';
      case 'movetype': return 'text-indigo-600 dark:text-indigo-400';
      case 'container': return 'text-cyan-600 dark:text-cyan-400';
      case 'chassis': return 'text-amber-600 dark:text-amber-400';
      case 'bol': return 'text-pink-600 dark:text-pink-400';
      case 'location': return 'text-rose-600 dark:text-rose-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  // Tinted gradient header strip per result type (DSM-style collapsible section header)
  const getSectionHeaderClass = (type: GlobalSearchResult['type']) => {
    switch (type) {
      case 'pro':
        return 'bg-gradient-to-r from-purple-50 to-purple-100 text-purple-800 border-purple-200 dark:from-purple-900/30 dark:to-purple-800/20 dark:text-purple-200 dark:border-purple-700/40';
      case 'driver':
        return 'bg-gradient-to-r from-green-50 to-green-100 text-green-800 border-green-200 dark:from-green-900/30 dark:to-green-800/20 dark:text-green-200 dark:border-green-700/40';
      case 'customer':
        return 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 border-blue-200 dark:from-blue-900/30 dark:to-blue-800/20 dark:text-blue-200 dark:border-blue-700/40';
      case 'dispatcher':
        return 'bg-gradient-to-r from-orange-50 to-orange-100 text-orange-800 border-orange-200 dark:from-orange-900/30 dark:to-orange-800/20 dark:text-orange-200 dark:border-orange-700/40';
      case 'driver_orders':
        return 'bg-gradient-to-r from-teal-50 to-teal-100 text-teal-800 border-teal-200 dark:from-teal-900/30 dark:to-teal-800/20 dark:text-teal-200 dark:border-teal-700/40';
      case 'movetype':
        return 'bg-gradient-to-r from-indigo-50 to-indigo-100 text-indigo-800 border-indigo-200 dark:from-indigo-900/30 dark:to-indigo-800/20 dark:text-indigo-200 dark:border-indigo-700/40';
      case 'container':
        return 'bg-gradient-to-r from-cyan-50 to-cyan-100 text-cyan-800 border-cyan-200 dark:from-cyan-900/30 dark:to-cyan-800/20 dark:text-cyan-200 dark:border-cyan-700/40';
      case 'chassis':
        return 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-800 border-amber-200 dark:from-amber-900/30 dark:to-amber-800/20 dark:text-amber-200 dark:border-amber-700/40';
      case 'bol':
        return 'bg-gradient-to-r from-pink-50 to-pink-100 text-pink-800 border-pink-200 dark:from-pink-900/30 dark:to-pink-800/20 dark:text-pink-200 dark:border-pink-700/40';
      case 'location':
        return 'bg-gradient-to-r from-rose-50 to-rose-100 text-rose-800 border-rose-200 dark:from-rose-900/30 dark:to-rose-800/20 dark:text-rose-200 dark:border-rose-700/40';
      default:
        return 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-800 border-gray-200 dark:from-gray-900/30 dark:to-gray-800/20 dark:text-gray-200 dark:border-gray-700/40';
    }
  };

  // Filter out Move Types (too broad) and group results by type. These feed
  // TanTable's `data`, so they must be memoized — a fresh array identity on
  // every render rebuilds the table's row model (and re-runs its filters/sort)
  // on routine interactions like clicking a PRO, which writes search params.
  const filteredResults = useMemo(
    () => results.filter(r => r.type !== 'movetype'),
    [results]
  );

  const sortedGroupEntries = useMemo(() => {
    const grouped = filteredResults.reduce((acc, result) => {
      const groupName = result.group;
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(result);
      return acc;
    }, {} as Record<string, GlobalSearchResult[]>);
    return Object.entries(grouped).sort(([a], [b]) =>
      (GROUP_ORDER[a] || 99) - (GROUP_ORDER[b] || 99)
    );
  }, [filteredResults]);

  // PRO rows per group, for that group's TanTable (both views — Multi Row
  // renders the same rows as cards). Scoped per group, NOT page-wide: a search
  // can return both "PRO Numbers" and "Templates", and each group's table must
  // show only its own results rather than the combined set.
  const proRowsByGroup = useMemo(() => {
    const byGroup = new Map<string, ProSearchData[]>();
    for (const [groupName, groupResults] of sortedGroupEntries) {
      // 'pro' only: templates render as cards (TEMPLATE_CARD_FIELDS), not in a
      // TanTable, so building rows for their group would be dead work.
      if (groupResults[0].type !== 'pro') continue;
      byGroup.set(
        groupName,
        groupResults.filter(r => r.type === 'pro').map(r => (r.data || {}) as ProSearchData)
      );
    }
    return byGroup;
  }, [sortedGroupEntries]);

  // Every PRO this search produced, deduped, for the Documents view. Templates
  // ("T110") drop out: Number() makes them NaN and the documents endpoints only
  // take numeric pronumbers. `> 0` is load-bearing on top of isFinite —
  // Number('') and Number(null) are both 0, which would otherwise send a
  // phantom "PRO 0" to the search endpoint.
  const allProNumbers = useMemo(() => {
    const seen = new Set<number>();
    for (const list of proRowsByGroup.values()) {
      for (const r of list) {
        const n = Number(r.pronumber);
        if (Number.isFinite(n) && n > 0) seen.add(n);
      }
    }
    return Array.from(seen);
  }, [proRowsByGroup]);

  // ── Inline order editing (Single Row View only) ──────────────────────────
  // Search Results is the widest surface onto the change-all pipeline, so what is
  // editable is decided by the backend, which mirrors the allowlist behind /pro's
  // "apply to all" checkboxes. Empty until it loads: no map, no affordance.
  const [gridEditFields, setGridEditFields] = useState<GridFieldMap>({});
  // Read by the column-edit toggle, which must not be rebuilt every time the field map
  // settles — it is handed to TanTable and a new identity there churns the menu.
  const gridEditFieldsRef = useRef<GridFieldMap>({});
  gridEditFieldsRef.current = gridEditFields;
  const staged = useStagedEdits();
  // Recomputed only when the staged set changes, so the row-class callback below is
  // not rebuilding a Set per row per render.
  const stagedKeys = useMemo(() => new Set(Object.keys(staged.edits)), [staged.edits]);
  const [savingGridEdits, setSavingGridEdits] = useState(false);
  const [gridEditResult, setGridEditResult] = useState<{ ok: number; failed: { pronumber: string; errors: string[] }[] } | null>(null);

  // The SECOND component gates a few fields carry on /pro. Small and static, so the
  // hooks are named rather than looped — add a line if FIELD_COMPONENTS grows. The
  // server enforces these too; withholding the cell just means the save is never the
  // first time the user is told no.
  const canEditRailbill = useCanEditRailbillField();
  const canEditWeights = useCanEditOrderWeightFields();
  const canEditLegs = useCanEditLegFields();
  const allowedComponents = useMemo(() => {
    const out = new Set<string>();
    if (canEditRailbill) out.add(RAILBILL_FIELD_COMPONENT_ID);
    if (canEditWeights) out.add(ORDER_WEIGHT_FIELDS_COMPONENT_ID);
    if (canEditLegs) out.add(LEG_EDITING_COMPONENT_ID);
    return out;
  }, [canEditRailbill, canEditWeights, canEditLegs]);

  // The config-driven display rules, applied per row by movetype. Fetched through the
  // shared cache the /pro form already uses.
  const [displayRules, setDisplayRules] = useState<OrderFieldDisplayRule[]>([]);
  useEffect(() => {
    if (!canEditOrderLcFields) return;
    let cancelled = false;
    fetchOrderFieldDisplayRules('cms')
      .then((r) => { if (!cancelled) setDisplayRules(r); })
      .catch(() => { /* the server still enforces them */ });
    return () => { cancelled = true; };
  }, [canEditOrderLcFields]);

  useEffect(() => {
    if (!canEditOrderLcFields) return;
    const ctrl = new AbortController();
    ordersService.changeAllGridFields(ctrl.signal)
      .then((f) => { if (!ctrl.signal.aborted) setGridEditFields(f); })
      .catch(() => { /* no map, no editing — the grid stays read-only */ });
    return () => ctrl.abort();
  }, [canEditOrderLcFields]);

  // A one-to-many field (an invoice line item, say) puts the same order on several
  // rows. Each would stage independently and the last one sent would silently win, so
  // editing is switched off while any duplicate is on screen rather than resolving it
  // at random. The picker already warns "(pros may dupe)" when such a field is added.
  // Duplicates are judged on what is ON SCREEN, not on everything fetched. Filtering
  // an expanding column down to one row per order (accessorialnum = 0, say) genuinely
  // resolves the ambiguity, and the user should get editing back without having to
  // remove the column they are filtering on. Each group's TanTable reports its own
  // filtered rows; the setter bails when the list is unchanged, so this cannot cycle
  // with the effect that feeds it.
  const [visibleProsByGroup, setVisibleProsByGroup] = useState<Record<string, string[]>>({});
  const handleGroupFilteredRows = useCallback((groupName: string, rows: ProSearchData[]) => {
    const pros = rows
      .map((r) => String((r as { pronumber?: unknown })?.pronumber ?? ''))
      .filter(Boolean);
    setVisibleProsByGroup((prev) => {
      const cur = prev[groupName];
      if (cur && cur.length === pros.length && cur.every((p, i) => p === pros[i])) return prev;
      return { ...prev, [groupName]: pros };
    });
  }, []);

  const dupePros = useMemo(() => {
    const rows: { pronumber?: unknown }[] = [];
    for (const [groupName, groupRows] of proRowsByGroup) {
      // Until a group has reported, judge it on everything fetched — erring toward
      // read-only rather than briefly offering an edit we cannot place.
      const visible = visibleProsByGroup[groupName];
      if (visible) rows.push(...visible.map((pronumber) => ({ pronumber })));
      else rows.push(...groupRows);
    }
    return duplicatedPros(rows);
  }, [proRowsByGroup, visibleProsByGroup]);

  // Which added column is responsible. A field definition marks itself `expands` when
  // its join is one-to-many — the same flag behind the picker's "(pros may dupe)"
  // warning and the single-branch rule above — so the culprit is nameable rather than
  // something the user has to find by removing columns one at a time.
  const expandingColumnNames = useMemo(
    () => additionalFields
      .map((fn) => defByName.get(fn))
      .filter((d): d is FieldDefinition => !!(d && fieldExpands(d) && d.join_key))
      .map((d) => d.displayname || d.fieldname),
    [additionalFields, defByName, fieldExpands],
  );

  // Whether the rows actually carry the invoice recno. Read off the data rather than
  // assumed from the request: if the field ever stops coming back, the cells go
  // read-only instead of staging edits that cannot be addressed.
  const hasInvoiceKey = useMemo(() => {
    for (const rows of proRowsByGroup.values()) {
      for (const row of rows) {
        if ((row as unknown as Record<string, unknown>)[INVOICE_RECNO_FIELD] != null) return true;
      }
    }
    return false;
  }, [proRowsByGroup]);

  // Which columns the user has switched into edit mode, from the column menu. Editing
  // is opt-in per column rather than always on: a grid people mostly READ should not
  // put an input under every cursor, and an accidental drag across a live column is a
  // much easier mistake to make than deliberately turning editing on first.
  const [editingColumns, setEditingColumns] = useState<ReadonlySet<string>>(new Set());
  const toggleColumnEditing = useCallback((columnId: string) => {
    setEditingColumns((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
        // Turning editing off ends that column's edit session, so what it staged is
        // undone with it. Leaving the changes pending behind a switch that says
        // editing is off would show a highlighted row and a count for a value the
        // cell no longer displays.
        const spec = specFor(columnId, gridEditFieldsRef.current);
        if (spec) {
          const prefix = keyPrefixForScope(spec.scope);
          staged.discardCells(
            (key, column) => column === spec.column && key.startsWith(prefix),
          );
        }
      } else {
        next.add(columnId);
      }
      return next;
    });
  }, [staged]);

  // Single Row View only: the Multi Row card is a read-only summary, and a card has
  // nowhere sensible to put a cell editor.
  const gridEditEnabled =
    effectiveViewMode === 'table' && canEditOrderLcFields
    && Object.keys(gridEditFields).length > 0;

  // Duplicated orders disable the ORDER-scoped fields only. A leg edit resolves through
  // the same order to the same leg, and an invoice edit is addressed by its own recno —
  // which is the normal shape of an accessorial list, not a fault to be fixed.
  const ordersDisabled = dupePros.length > 0;

  // Identity of the current search run — the same inputs the fetch effect keys on.
  // Used to remount the progress indicator so its elapsed clock restarts per request.
  const searchRunKey = [
    query, filterJson, searchBy, entityType, isClosed, customIncludeDeleted,
    additionalFieldsKey, refetchNonce,
  ].join('|');

  // One documents request can only carry so many PROs, and an uncapped Custom
  // Filter can legitimately return more than that.
  const docsOverCap = allProNumbers.length > MAX_SEARCH_PROS;

  // A new search means a new document universe — drop back to the orders grid
  // rather than leaving the user on documents for results they didn't ask for.
  // Keyed on the search IDENTITY (not refetchNonce), so a bulk-apply refetch of
  // the same search doesn't kick them out of the view.
  useEffect(() => {
    setDocsView(false);
  }, [query, isMulti, isClosed, entityType, searchBy, filterJson, customIncludeDeleted]);

  // A per-row trace finished (the ➡️ on the SSLine / Terminal cells). The trace
  // has already written the order — LFD, Available, Out Gate, Trace Date, Vessel,
  // Customs and friends — so the row on screen is now stale in exactly the fields
  // the user ran the trace to see. Re-read just that order and swap its payload in
  // place; everything downstream (proRowsByGroup, the cards, the grid) derives from
  // `results`, so one setState refreshes whichever view is open.
  //
  // The re-read carries the CURRENT extra-column selection. Trace Date is an added
  // column, not part of the base PRO payload, and it is the column a user most
  // naturally adds for this feature — refreshing without it left the one field the
  // trace had just written still showing its pre-trace value.
  //
  // Merged OVER the existing row so any key the re-read doesn't carry survives, and
  // paired positionally so a row-expanding column (invoice line items) updates every
  // fanned row rather than collapsing them. Staged grid edits are unaffected — those
  // live in their own store and still win in the cell.
  const additionalFieldsRef = useRef<string[]>([]);
  additionalFieldsRef.current = additionalFields;
  const handleRowTraced = useCallback(async (pronumber: number) => {
    const fresh = await searchService.refreshProRows(pronumber, additionalFieldsRef.current);
    if (!fresh.length) return;
    setResults((prev) => mergeRefreshedProRows(
      prev,
      (r) => r.type === 'pro' && isProRow(r.value, pronumber),
      fresh,
      (r, next) => ({ ...r, data: { ...(r.data || {}), ...next } }),
    ));
  }, []);

  // Columns for the PRO table. Core fields are visible; the rest are available
  // via TanTable's column-visibility menu (hidden by default below). In card
  // mode this list is the card's field list too: visibility decides WHICH fields
  // a card shows and column order decides WHERE — a column id here is the id
  // ProResultCard looks up in its field catalog (falling back to the header
  // label + the raw row value for ids the catalog doesn't know).
  const proColumns = useMemo<ColumnDef<ProSearchData>[]>(() => {
    // The static ~40-column PRO set, shared with the LC Dashboards results view.
    // Each static column that already renders a registry field wears that field's
    // names — the short one here as the header, the long one in the picker below —
    // so "Terminate Destination - Location" reads the same on this grid as on the
    // dashboards instead of as a local "Return To". Ids are untouched, so saved
    // layouts and the card catalog still resolve. (The LC Dashboards card view gets
    // the same treatment from useAdditionalFieldColumns, off the same map.)
    const staticColumns = buildProCardColumns(handleProClick, { onTraced: handleRowTraced });
    const base = applyRegistryTitles(
      staticColumns,
      resolveRegistryTitles(
        staticColumns as ReadonlyArray<{ id?: string; meta?: unknown }>,
        fieldDefinitions,
      ),
    );

    // User-selected extra columns (invoice / line-item / any orders field). Each
    // selected fieldname becomes a generated column whose accessorKey is
    // getApiFieldName(fieldname) — usually the fieldname itself (matching the
    // backend's flat `AS <fieldname>` enrichment key), but remapped for the
    // handful of names in field_name_mapping.
    const extra = additionalFields
      .map((fn) => defByName.get(fn))
      .filter((d): d is FieldDefinition => !!d)
      .map((d) => generateColumnDef(d) as ColumnDef<ProSearchData>);
    return extra.length ? [...base, ...extra] : base;
  }, [handleProClick, handleRowTraced, additionalFields, defByName, fieldDefinitions]);

  // What those column ids MEAN, off the same map and the same rules that just gave
  // them their titles. The static PRO set is named for what each column shows
  // ("Return To", "LFD") rather than for the field behind it, so a View saved on an
  // LC bucket card — where the columns are id'd `t_destname` / `lastfreedaydate` —
  // has nothing to match on without this, and Load View refuses it as "columns
  // unavailable" while every one of them is on screen under another name.
  const proFieldAliases = useMemo(
    () => resolveFieldAliases(proColumns, fieldDefinitions),
    [proColumns, fieldDefinitions],
  );

  // The same columns, with the editable ones swapped for an in-place editor. Kept
  // separate from `proColumns` so the picker, the card view and the saved layouts all
  // keep seeing the plain definitions — only what the Single Row grid renders changes.
  const proEditableColumns = useMemo<ColumnDef<ProSearchData>[]>(() => {
    if (!gridEditEnabled) return proColumns;
    return proColumns.map((col) => {
      const id = (col as { id?: string; accessorKey?: string }).id
        ?? (col as { accessorKey?: string }).accessorKey;
      if (!id) return col;
      const meta = gridEditMetaFor(id, gridEditFields, {
        ordersDisabled, hasInvoiceKey, allowedComponents,
      });
      if (!meta) return col;
      const spec = specFor(id, gridEditFields)!;

      // Offered, but not live until the user turns this column on. The bulk-edit meta
      // rides along either way so the drag affordance follows the same switch.
      if (!editingColumns.has(id)) return col;

      return {
        ...col,
        meta: { ...((col as { meta?: object }).meta ?? {}), bulkEdit: meta },
        cell: ({ row }: { row: { original: ProSearchData } }) => {
          const data = row.original as unknown as Record<string, unknown>;
          // The key carries the scope, so an order edit and an invoice edit on the
          // same row are filed separately instead of overwriting each other.
          const key = stagingKey(spec, data);
          // This order's movetype may put the field out of reach even though the user
          // may write it — /pro hides or locks it, so the grid must not offer it.
          const blocked = spec.scope === 'order'
            && displayBlockedForRow(displayRules, data.movetype).has(spec.column);
          if (blocked) return <span className="text-gray-400">{String(data[id] ?? '')}</span>;
          return (
            <EditableCell
              value={key ? staged.effective(key, spec.column, data[id]) : data[id]}
              meta={meta}
              edited={!!key && staged.isEdited(key, spec.column)}
              disabled={savingGridEdits || !key}
              onCommit={(next) => key && staged.stage(key, spec.column, next, data[id])}
            />
          );
        },
      } as ColumnDef<ProSearchData>;
    });
  }, [proColumns, gridEditEnabled, gridEditFields, ordersDisabled, hasInvoiceKey,
      allowedComponents, displayRules, editingColumns, staged, savingGridEdits]);

  // Drag-select a range in an editable column — stages it, same as typing in a cell.
  const handleProBulkEdit = useCallback((columnId: string, rows: ProSearchData[], value: unknown) => {
    const spec = specFor(columnId, gridEditFields);
    if (!spec) return;
    const entries = rows
      .map((r) => {
        const data = r as unknown as Record<string, unknown>;
        const key = stagingKey(spec, data);
        // The stored value rides along so a drag over rows that already hold it
        // stages nothing for them.
        return key ? { recno: key, original: data[columnId] } : null;
      })
      .filter((e): e is { recno: string; original: unknown } => !!e);
    if (entries.length) staged.stageMany(entries, spec.column, value);
  }, [staged, gridEditFields]);

  // Save every staged order in ONE request. Each runs the saverules engine in its own
  // transaction server-side, so a rejected order leaves the rest applied and comes back
  // named — those stay staged for a retry.
  const handleSaveGridEdits = useCallback(async () => {
    if (!staged.changes.length || savingGridEdits) return;
    setSavingGridEdits(true);
    setGridEditResult(null);
    try {
      const { results } = await ordersService.changeAllGridEdit({
        ...buildPayload(staged.changes),
        submitNonce: `sr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      });
      const failed = results
        .filter((r) => !r.applied)
        .map((r) => ({ pronumber: String(r.pronumber), errors: r.errors ?? [] }));
      setGridEditResult({ ok: results.length - failed.length, failed });
      // Keys embed the pronumber for every scope, so the failures map back without a
      // row lookup — the rows may have been refetched by the time this runs.
      if (failed.length) {
        staged.keepOnly(keysForPros(Object.keys(staged.edits), failed.map((f) => f.pronumber)));
      } else {
        staged.discard();
      }
      setRefetchNonce((n) => n + 1);
    } catch (err: any) {
      setGridEditResult({
        ok: 0,
        failed: [{ pronumber: '', errors: [err?.response?.data?.detail || 'Save failed.'] }],
      });
    } finally {
      setSavingGridEdits(false);
    }
  }, [staged, savingGridEdits]);

  // Default visibility per view — the two views keep separate layouts (separate
  // settingsKeys below), so they get separate defaults too. Single Row opens on
  // the narrow core set; Multi Row opens on every field the classic banded card
  // showed, matching the pre-card-mode view it replaced. User-added extras stay
  // visible in both so a just-added field actually shows up.
  const proDefaultColumnVisibility = useMemo<{ table: VisibilityState; cards: VisibilityState }>(() => {
    // Column ids are api names, so the selected db fieldnames have to be mapped
    // before they can be compared (dispatchername -> dispatcher_name, ...).
    const added = new Set(additionalFields.map(getApiFieldName));
    const build = (visibleIds: readonly string[]): VisibilityState => {
      const keep = new Set(visibleIds);
      const vis: VisibilityState = {};
      for (const col of proColumns) {
        const id = (col as { id?: string; accessorKey?: string }).id
          ?? (col as { accessorKey?: string }).accessorKey;
        if (id && !keep.has(id) && !added.has(id)) vis[id] = false;
      }
      return vis;
    };
    return {
      table: build(PRO_CARD_CORE_VISIBLE_IDS),
      cards: build([...PRO_CARD_FIELD_IDS, ...PRO_CARD_HEADER_COLUMN_IDS]),
    };
  }, [proColumns, additionalFields]);

  // Picker universe = the built-in static columns (so they stay show/hide-able —
  // the picker's dynamic mode lists availableFields only) PLUS every displayable
  // orders-scope field def that a built-in column doesn't already render. See
  // buildProPickerFields for the two-rule de-duplication. `expands` drives the
  // "(pros may dupe)" warning.
  const availableProFields = useMemo(
    () => buildProPickerFields({
      columns: proColumns as ReadonlyArray<{ id?: string; accessorKey?: string; header?: unknown }>,
      // `expands` is folded here rather than inside buildProPickerFields so the picker
      // module stays surface-agnostic: it renders the warning for whatever it is
      // handed, and this page decides whether legs are fanning right now.
      fieldDefinitions: legFanActive
        ? fieldDefinitions.map((d) => (d.expands_leg_fan && !d.expands ? { ...d, expands: true } : d))
        : fieldDefinitions,
      additionalFields,
    }),
    [proColumns, fieldDefinitions, additionalFields, legFanActive],
  );

  // Card renderer for Multi Row View. Hoisted out of the render body because
  // TanTable feeds it into the row virtualizer's estimateSize deps — a new
  // closure each render would hand useVirtualizer a new estimator every time.
  //
  // `ctx.visibleColumns` is the whole contract: the visible columns in COLUMN
  // ORDER, minus the header-owned ids above. It carries the user-added dynamic
  // columns and any static column with no hand-written card entry (e.g. the
  // service-failure fields) for free, so the card shows exactly what the column
  // picker says it shows — and a chip drag repositions the field on every card.
  const renderProCard = useCallback(
    (
      d: ProSearchData,
      ctx: {
        visibleColumns: { id: string; label: string }[];
        renderCell: (columnId: string) => React.ReactNode;
      },
    ) => (
      <ProResultCard
        as="div"
        result={{ type: 'pro', value: String(d.pronumber), label: String(d.pronumber), group: 'PROs', data: d }}
        onProClick={handleProClick}
        // `render` per column, as the LC dashboards' card view already does: the
        // catalog getter still wins for all but the `hostRender` fields (the SSLine
        // and Terminal cells), which need the host's renderer to keep their
        // run-trace button on the card.
        orderedFields={ctx.visibleColumns.map((c) => ({ ...c, render: () => ctx.renderCell(c.id) }))}
      />
    ),
    [handleProClick],
  );

  return (
    <FieldHighlightProvider>
    <div className="h-full flex flex-col">
      {/* Breadcrumb */}
      <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700">
        <nav className="text-sm text-muted">
          <span
            onClick={() => navigate('/home')}
            className="hover:text-gray-700 dark:hover:text-gray-300 cursor-pointer"
          >
            Home
          </span>
          <span className="mx-2">&gt;</span>
          <span className="text-body">Search Results</span>
        </nav>
      </div>

      {/* Unsaved order edits. Pinned above the results and deliberately loud: staging
          is what makes a mis-drag recoverable, but only if the user can see that
          nothing has been written yet. */}
      {staged.recordCount > 0 && (
        <div className="mx-6 mt-3 flex flex-wrap items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
          <span className="font-semibold">
            {staged.cellCount} unsaved change{staged.cellCount === 1 ? '' : 's'} on{' '}
            {staged.recordCount} order{staged.recordCount === 1 ? '' : 's'}
          </span>
          <span className="text-xs text-amber-800 dark:text-amber-300">
            Nothing is written until you save.
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              onClick={staged.discard}
              disabled={savingGridEdits}
              className="rounded border border-amber-400 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 disabled:opacity-50 dark:border-amber-600 dark:bg-gray-900 dark:text-amber-200"
            >
              Discard
            </button>
            <button
              type="button"
              onClick={handleSaveGridEdits}
              disabled={savingGridEdits}
              className="rounded border border-blue-600 bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {savingGridEdits
                ? 'Saving…'
                : `Save ${staged.recordCount} order${staged.recordCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      )}

      {/* Outcome of the last save. Failures name the orders and stay staged. */}
      {gridEditResult && (
        <div
          className={`mx-6 mt-3 flex items-start justify-between gap-3 rounded-md border px-3 py-2 text-sm ${
            gridEditResult.failed.length
              ? 'border-red-300 bg-red-50 text-red-800 dark:border-red-700/50 dark:bg-red-900/20 dark:text-red-200'
              : 'border-green-300 bg-green-50 text-green-800 dark:border-green-700/50 dark:bg-green-900/20 dark:text-green-200'
          }`}
        >
          <span>
            {gridEditResult.ok > 0 && `${gridEditResult.ok} order${gridEditResult.ok === 1 ? '' : 's'} updated. `}
            {gridEditResult.failed.slice(0, 3).map((f) => (
              <span key={f.pronumber} className="block">
                {f.pronumber ? `#${f.pronumber}: ` : ''}{f.errors.join(' · ') || 'Failed.'}
              </span>
            ))}
            {gridEditResult.failed.length > 3 && (
              <span className="block">…and {gridEditResult.failed.length - 3} more.</span>
            )}
          </span>
          <button
            type="button"
            onClick={() => setGridEditResult(null)}
            className="shrink-0 font-medium"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* One order on several rows makes inline editing ambiguous — see
          duplicatedPros. The grid stays read-only until the duplicating column goes. */}
      {gridEditEnabled && ordersDisabled && (
        <div className="mx-6 mt-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
          Order fields are read-only: {dupePros.length} order{dupePros.length === 1 ? '' : 's'}{' '}
          appear{dupePros.length === 1 ? 's' : ''} on more than one row, so an edit could not
          be matched to a single order. Invoice and leg columns are still editable, since
          those are addressed by their own record.{' '}
          {expandingColumnNames.length > 0 ? (
            <>
              Remove{' '}
              <b>
                {expandingColumnNames.length === 1
                  ? expandingColumnNames[0]
                  : `${expandingColumnNames.slice(0, -1).join(', ')} and ${expandingColumnNames.slice(-1)[0]}`}
              </b>
              , or filter the results down to one row per order.
            </>
          ) : (
            'Filter the results down to one row per order to edit here.'
          )}
        </div>
      )}

      {/* Auto-dropped-column note (e.g. a line-item column from a different
          invoice group that would have duplicated rows). */}
      {columnNote && (
        <div className="mx-6 mt-3 flex items-start justify-between gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
          <span>{columnNote}</span>
          <button
            type="button"
            onClick={() => setColumnNote(null)}
            className="shrink-0 font-medium text-amber-700 hover:text-amber-900 dark:text-amber-300"
            aria-label="Dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
          <h1
            className="text-xl font-semibold text-body"
            title={filterSummary.length ? filterSummary.join('\n') : undefined}
          >
            {filterJson
              ? `Custom Filter — ${filterSummary.length} condition${filterSummary.length === 1 ? '' : 's'}`
                + (customIncludeDeleted ? ' • incl. deleted' : '')
              : searchBy
              ? `Custom Search — ${customSearchLabel(searchBy)}`
                + ` (${customTerms.length} ${customTerms.length === 1 ? 'value' : 'values'})`
                + (customIncludeDeleted ? ' • incl. deleted' : '')
              : entityType
              ? entityOrdersTitle(ordersBy, ordersLabel, query)
              : isMulti
                ? `Batch Search — ${multiTerms.length} terms`
                  + (termChips.length < multiTerms.length ? ` (${termChips.length} unique)` : '')
                : `Search Results for "${query}"`
                  + (isClosed ? ' • incl. closed & deleted' : '')
            }
          </h1>
          {/* Scope control for a plain search (the other modes carry their own:
              Custom Filter/Search have an includeDeleted checkbox, and both are
              permanently all-scope anyway). Always offered — not just when the
              search came up empty — because a query can match one record type
              while the record actually wanted is a closed or deleted one.

              The two states are deliberately DIFFERENT shapes, because the header
              already holds status chips ("View: <name>", and the active state
              below) built from this same rounded-border-px-2-py-1 recipe. The
              inactive control was first written that way too and labelled "Include
              closed & deleted" — which read as "includes closed & deleted", i.e. a
              statement that they were already in the results, rather than a button
              that would add them. So: the ACTION is verb-first with a magnifier and
              the blue used for actions elsewhere (the same wording as the header
              dropdown's link, for one action with one name), and only the ACTIVE
              STATE is a status chip with a dismiss ✕, matching the View chip. */}
          {isPlainSearch && (
            isClosed ? (
              <span className="inline-flex shrink-0 items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800 dark:border-amber-700/50 dark:bg-amber-900/20 dark:text-amber-200">
                <span title="Closed orders and soft-deleted orders and templates are included in these results">
                  Closed &amp; deleted included
                </span>
                <button
                  type="button"
                  onClick={() => setClosedScope(false)}
                  aria-label="Stop including closed and deleted records"
                  title="Back to open orders only"
                  className="rounded p-0.5 hover:bg-amber-100 dark:hover:bg-amber-800/40"
                >
                  <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setClosedScope(true)}
                title="Re-run this search against closed orders and soft-deleted orders and templates"
                className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100 hover:border-blue-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:bg-blue-900/40"
              >
                <MagnifyingGlassIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Also search closed &amp; deleted
              </button>
            )
          )}
          {/* Refine the filter that produced this view, in place. */}
          {filterJson && (
            <CustomFilterEditButton
              filter={filterJson}
              includeDeleted={customIncludeDeleted}
              viewId={viewIdParam}
              onApply={handleFilterEdited}
              summary={filterSummary}
            />
          )}
          {/* The View on loan, with the way out of it. Dropping it restores the
              user's own layout for this grid — nothing of theirs was overwritten. */}
          {viewActive && (
            <span className="inline-flex shrink-0 items-center gap-1.5 rounded border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-700 dark:border-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300">
              <span
                className="truncate"
                title={
                  activeView!.kind === 'view'
                    ? `Published by ${activeView!.publishedBy}`
                    : 'Your saved layout, applied to this search only'
                }
              >
                View: {activeView!.name}
              </span>
              <button
                type="button"
                onClick={clearActiveView}
                aria-label={`Stop using the View "${activeView!.name}"`}
                title="Back to your own layout"
                className="rounded p-0.5 hover:bg-indigo-100 dark:hover:bg-indigo-800/40"
              >
                <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          )}
          {/* A shared link whose View has since been deleted (or whose snapshot is
              unusable) silently uses the user's own layout — say so rather than
              leaving them to wonder why the link looks different. */}
          {viewUnresolved && (
            <span className="shrink-0 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              That View is no longer available — showing your own layout
            </span>
          )}
          </div>
          <div className="flex items-center gap-4">
            {/* Orders grid <-> Documents view. Same button both ways: it names
                the view it switches TO, and reads pressed while on documents. */}
            {allProNumbers.length > 0 && (
              <button
                type="button"
                onClick={() => setDocsView((v) => !v)}
                // The Custom Filter is uncapped (it backs large exports), so a result
                // set can legitimately exceed what ONE documents request accepts. The
                // endpoint would answer with a raw 422, so stop here and say why.
                disabled={!docsView && docsOverCap}
                aria-label="Toggle documents view"
                aria-pressed={docsView}
                className={`flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                  docsView
                    ? 'border-purple-600 bg-purple-600 text-white'
                    : 'border-gray-300 bg-panel text-gray-600 hover:bg-highlight dark:border-gray-600 dark:text-gray-300'
                }`}
                title={docsView
                  ? 'Back to the orders grid'
                  : docsOverCap
                    ? `These results cover ${allProNumbers.length.toLocaleString()} orders — `
                      + `the Documents view takes at most ${MAX_SEARCH_PROS.toLocaleString()}. `
                      + 'Narrow the search to use it.'
                    : `Documents on all ${allProNumbers.length} order(s) in these results`}
              >
                <DocumentTextIcon className="h-4 w-4" />
                {docsView ? 'Orders' : 'Documents'}
              </button>
            )}
            {/* Cards / Table view toggle */}
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                type="button"
                onClick={() => selectViewMode('cards')}
                aria-pressed={effectiveViewMode === 'cards'}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors ${
                  effectiveViewMode === 'cards'
                    ? 'bg-purple-600 text-white'
                    : 'bg-panel text-gray-600 dark:text-gray-300 hover:bg-highlight'
                }`}
                title="Show each result as a multi-row card"
              >
                <Squares2X2Icon className="h-4 w-4" />
                Multi Row View
              </button>
              <button
                type="button"
                onClick={() => selectViewMode('table')}
                aria-pressed={effectiveViewMode === 'table'}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors border-l border-gray-300 dark:border-gray-600 ${
                  effectiveViewMode === 'table'
                    ? 'bg-purple-600 text-white'
                    : 'bg-panel text-gray-600 dark:text-gray-300 hover:bg-highlight'
                }`}
                title="Show each result as a single table row"
              >
                <TableCellsIcon className="h-4 w-4" />
                Single Row View
              </button>
            </div>
            <span className="text-sm text-muted">
              {filteredResults.length} results
            </span>
            {/* Anchors the `order-create` permissions cog (self-registering,
                same pattern as ProDetail's template-edit-fields anchor);
                `defaultDisabled` keeps today's behavior (hidden) until an
                admin grants a group access. */}
            <PermissionsContainer
              componentId={ORDER_CREATE_COMPONENT_ID}
              componentName="Create Order"
              defaultDisabled
            >
              {canCreateOrder && (
                <button
                  type="button"
                  onClick={openCreateOrder}
                  className="px-3.5 py-1.5 text-sm font-semibold bg-blue-600 text-white border border-blue-700 rounded-md cursor-pointer inline-flex items-center gap-1.5 shadow-sm hover:bg-blue-700"
                >
                  <PlusIcon className="h-4 w-4" />
                  Create New Order
                </button>
              )}
            </PermissionsContainer>
          </div>
        </div>
        {isMulti && <TermChips chips={termChips} />}
      </div>

      {/* Content */}
      {/* The Documents view owns its own scrolling (its grid virtualizes and
          its action bar sticks), so it needs a definite height — hence
          overflow-hidden + no padding instead of the orders list's scroller. */}
      <div className={docsView ? 'min-h-0 flex-1 overflow-hidden pb-3' : 'flex-1 overflow-auto py-3'}>
        {isLoading ? (
          <SearchProgress
            // Remount per search so the clock always times THIS request. Without it a
            // re-running effect (extra columns resolving, a refetch) leaves the old
            // clock running, and the number shown covers two requests — which is how a
            // 230s budget appeared to reach 252s.
            key={searchRunKey}
            active
            // Only the Custom Filter is uncapped, so only it warrants a "be patient"
            // note; a plain lookup running for minutes would be a fault, not expected.
            longRunning={!!filterJson}
            budgetSeconds={filterJson ? CUSTOM_FILTER_BUDGET_SECONDS : undefined}
          />
        ) : error ? (
          <div className="flex items-center justify-center h-32">
            <div className="text-red-500">{error}</div>
          </div>
        ) : docsView && allProNumbers.length > 0 ? (
          <SearchDocumentsView
            pros={allProNumbers}
            onProClick={handleProClick}
            defaultRecipient={currentUserEmail}
          />
        ) : filteredResults.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-32">
            <div className="text-muted">
              {filterJson
                ? 'No orders match this filter'
                : searchBy
                ? `No orders match those ${customSearchLabel(searchBy)} values`
                : `No results found for "${query}"`}
            </div>
            <div className="text-sm text-gray-400 dark:text-gray-500 mt-2">
              {filterJson || searchBy ? 'Try relaxing a condition' : 'Try a different search term'}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {sortedGroupEntries.map(([groupName, groupResults]) => {
              const firstResult = groupResults[0];
              const Icon = getIconForType(firstResult.type);
              const headerClass = getSectionHeaderClass(firstResult.type);
              const expanded = !collapsedGroups.has(groupName);

              return (
                <div key={groupName}>
                  {/* Collapsible section header (DSM style) */}
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupName)}
                    className={`w-full px-4 py-2 flex items-center gap-2 border-y font-semibold text-sm cursor-pointer ${headerClass}`}
                  >
                    <ChevronRightIcon
                      className={`h-5 w-5 transition-transform ${expanded ? 'rotate-90' : ''}`}
                    />
                    <Icon className="h-5 w-5" />
                    <span>{groupName}</span>
                    <span className="text-sm font-normal opacity-80">
                      ({groupResults.length})
                    </span>
                  </button>

                  {/* PRO groups render a TanTable in both views — Multi Row View
                      switches it into card mode via renderCardRow, so
                      filters/search/sort/export/action-bar work in either one.
                      The two views keep SEPARATE saved layouts (own settingsKey,
                      own defaults), so the wide card field set can't widen the
                      table. `key` remounts on toggle so the right layout loads;
                      the trade-off is that filters/sort do not carry across.

                      A published View (?view=<id>) is applied by handing its
                      snapshot in as initialSettings with settingsReadOnly on: the
                      grid renders the manager's layout but writes nothing, so the
                      user's own saved layout for this grid survives untouched and
                      returns the moment the View is dropped. The view id joins
                      `key` so switching Views remounts and reloads cleanly.
                      enableViews adds the manager-only Save As View button. */}
                  {expanded && firstResult.type === 'pro' && (
                    <div className="px-3 pb-2">
                      <TanTable<ProSearchData>
                        key={`${effectiveViewMode}:${viewActive ? activeView!.id : 'own'}`}
                        data={proRowsByGroup.get(groupName) ?? EMPTY_PRO_ROWS}
                        columns={proEditableColumns}
                        trackViewedColumnId="pronumber"
                        settingsKey={effectiveViewMode === 'cards' ? 'search-results-pro-cards' : 'search-results-pro-table'}
                        fieldName={effectiveViewMode === 'cards' ? 'search-results-pro-cards' : 'search-results-pro-table'}
                        cardTitle="PRO Numbers"
                        searchPlaceholder="Filter PRO results..."
                        defaultColumnVisibility={effectiveViewMode === 'cards' ? proDefaultColumnVisibility.cards : proDefaultColumnVisibility.table}
                        availableFields={availableProFields}
                        additionalFields={additionalFields}
                        onAdditionalFieldsChange={handleAdditionalFieldsChange}
                        fieldAliases={proFieldAliases}
                        enableViews
                        {...(viewActive
                          ? {
                              initialSettings: viewSnapshot,
                              settingsReady: true,
                              settingsReadOnly: true,
                              onReadOnlyLayoutSaved: clearActiveView,
                            }
                          : {})}
                        enableColumnFilters
                        enableExcelExport
                        enableGrouping={false}
                        seedLayoutFromCache
                        // The banners above this grid (unsaved changes, save result,
                        // duplicate-order notice, the table notice) change height as
                        // they come and go; a fixed card height would push the
                        // horizontal scrollbar under the footer exactly when there is
                        // most to scroll.
                        autoHeight
                        lockedColumns={[{ id: 'pronumber', side: 'left' }]}
                        getRowClassName={(row, m) =>
                          // A pending edit tints the whole row: one amber cell is easy
                          // to lose in a grid this wide, and the row may be the only
                          // part of it still on screen. Deleted still wins — that is
                          // the more important thing to notice about a row.
                          row.deleted === 'Y'
                            ? deletedRowClass(true, m.zebra)
                            : rowHasStagedEdit(row as unknown as Record<string, unknown>, stagedKeys)
                              ? 'bg-amber-50 dark:bg-amber-900/20'
                              : deletedRowClass(false, m.zebra)
                        }
                        bulkActions={proBulkActions}
                        {...(gridEditEnabled
                          ? {
                              onBulkEdit: handleProBulkEdit,
                              canEditColumn: (columnId: string) =>
                                !!gridEditMetaFor(columnId, gridEditFields, {
                                  ordersDisabled, hasInvoiceKey, allowedComponents,
                                }),
                              isColumnEditing: (columnId: string) => editingColumns.has(columnId),
                              onToggleColumnEditing: toggleColumnEditing,
                            }
                          : {})}
                        {...(effectiveViewMode === 'table' && canEditOrderLcFields
                          ? { onFilteredRowsChange: (rows: ProSearchData[]) => handleGroupFilteredRows(groupName, rows) }
                          : {})}
                        renderCardRow={effectiveViewMode === 'cards' ? renderProCard : undefined}
                        cardHeaderColumnIds={PRO_CARD_HEADER_COLUMN_IDS}
                      />
                    </div>
                  )}

                  {/* Templates: the same card as a PRO result, with a template's
                      field set (TEMPLATE_CARD_FIELDS). They used to render in the
                      PRO TanTable — correct payload shape, wrong shape of thing:
                      ~40 order columns, essentially all empty on a template. The
                      card also gets the red deleted tint and the Deleted badge for
                      free, which is what a template search mostly turns up. */}
                  {expanded && firstResult.type === 'template' && (
                    <ul className="bg-panel rounded-b-lg shadow-sm py-1">
                      {groupResults.map((result) => (
                        <ProResultCard
                          key={`template-${result.value}`}
                          result={result}
                          onProClick={handleProClick}
                          orderedFields={TEMPLATE_CARD_FIELDS}
                        />
                      ))}
                    </ul>
                  )}

                  {/* Results List (non-PRO groups: drivers/locations/etc.) */}
                  {expanded && !isProLike(firstResult.type) && (
                  <ul className="divide-y divide-gray-100 dark:divide-gray-700 bg-panel rounded-b-lg shadow-sm">
                    {groupResults.map((result) => {
                      // Driver result with action buttons
                      if (result.type === 'driver') {
                        return (
                          <li
                            key={`${result.type}-${result.value}`}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-body">
                                {result.label}<MuleBadge isMule={result.data?.isMule} />
                              </div>
                              {result.description && (
                                <div className="text-xs text-muted truncate">
                                  {result.description}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => handleDriverMapClick(result.value)}
                                className="p-1.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 text-blue-600 dark:text-blue-400 transition-colors"
                                title="View on Map"
                              >
                                <MapIcon className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleDriverDSMClick(result.value)}
                                className="px-2 py-1 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-800/50 transition-colors"
                                title="View in Driver Status Monitor"
                              >
                                DSM
                              </button>
                            </div>
                          </li>
                        );
                      }

                      // Location result - navigate to Locations page
                      if (result.type === 'location') {
                        return (
                          <li
                            key={`${result.type}-${result.value}`}
                            onClick={handleLocationClick}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-body">
                                {result.label}
                              </div>
                              {result.description && (
                                <div className="text-xs text-muted truncate">
                                  {result.description}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              View in Locations
                            </div>
                          </li>
                        );
                      }

                      // Container result - click opens Order Detail Drawer
                      if (result.type === 'container') {
                        return (
                          <li
                            key={`${result.type}-${result.value}`}
                            onClick={() => handleContainerClick(result)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-body">
                                {result.label}
                              </div>
                              {result.description && (
                                <div className="text-xs text-muted truncate">
                                  {result.description}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              Click to view order details
                            </div>
                          </li>
                        );
                      }

                      // Chassis result - click opens Order Detail Drawer via associated PRO
                      if (result.type === 'chassis') {
                        return (
                          <li
                            key={`${result.type}-${result.value}`}
                            onClick={() => handleChassisClick(result)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-body">
                                {result.label}
                              </div>
                              {result.description && (
                                <div className="text-xs text-muted truncate">
                                  {result.description}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              Click to view order details
                            </div>
                          </li>
                        );
                      }

                      // BOL result - click opens Order Detail Drawer via associated PRO
                      if (result.type === 'bol') {
                        return (
                          <li
                            key={`${result.type}-${result.value}`}
                            onClick={() => handleBolClick(result)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-body">
                                {result.label}
                              </div>
                              {result.description && (
                                <div className="text-xs text-muted truncate">
                                  {result.description}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              Click to view order details
                            </div>
                          </li>
                        );
                      }

                      // Quote result - click opens the quote editor drawer.
                      if (result.type === 'quote') {
                        return (
                          <li
                            key={`${result.type}-${result.value}`}
                            onClick={() => handleQuoteClick(result.value)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-body">
                                {result.label}
                              </div>
                              {result.description && (
                                <div className="text-xs text-muted truncate">
                                  {result.description}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              Click to open quote
                            </div>
                          </li>
                        );
                      }

                      // Aggregate order rows — load that entity's orders into
                      // this page's grid rather than opening V1 legacy CMS.
                      if (
                        result.type === 'driver_orders'
                        || result.type === 'customer'
                        || result.type === 'dispatcher'
                      ) {
                        return (
                          <li
                            key={`${result.type}-${result.value}`}
                            onClick={() => handleEntityOrdersClick(result)}
                            className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex items-center justify-between"
                          >
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-body">
                                {result.label}{result.type === 'driver_orders' && <MuleBadge isMule={result.data?.isMule} />}
                              </div>
                              {result.description && (
                                <div className="text-xs text-muted truncate">
                                  {result.description}
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 dark:text-gray-500">
                              Click to load these orders
                            </div>
                          </li>
                        );
                      }

                      // Other results - clickable to open legacy CMS
                      return (
                        <li
                          key={`${result.type}-${result.value}`}
                          onClick={() => handleLegacyClick(result)}
                          className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer flex items-center justify-between"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-body">
                              {result.label}
                            </div>
                            {result.description && (
                              <div className="text-xs text-muted truncate">
                                {result.description}
                              </div>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 dark:text-gray-500">
                            Opens in CMS
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Order Details Drawer */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={!!openPro && selectedPro !== null && !changeAllPros}
        onClose={handleCloseDrawer}
      />

      {/* Change All — opens the drawer on the first filtered pro as the anchor, in
          change-all mode; flagged fields fan out to the rest on Save. */}
      <ProDetailSlideOver
        pronumber={changeAllPros && changeAllPros.length > 0 ? changeAllPros[0] : null}
        isOpen={!!changeAllPros}
        onClose={() => {
          setChangeAllPros(null);
          // The fan-out may have changed other pros — refetch the grid so it reflects them.
          setRefetchNonce((n) => n + 1);
        }}
        changeAll={changeAllPros ? { targetPros: changeAllPros } : null}
      />

      {/* Quote editor drawer — opened for a single quote result (?quote=). */}
      <QuoteLaneEditorDrawer
        open={!!selectedQuoteLane}
        lane={selectedQuoteLane}
        onClose={handleCloseQuoteDrawer}
      />

      {/* Change Template for All — bulk apply to the visible/filtered pros. */}
      <ChangeTemplateBulkModal
        isOpen={bulkOpen}
        pros={bulkPros}
        onClose={() => setBulkOpen(false)}
        onApplied={() => setRefetchNonce((n) => n + 1)}
      />
    </div>
    </FieldHighlightProvider>
  );
};

const SearchResults: React.FC = () => (
  <ViewedProsProvider>
    {/* Owns the trace result modal for the whole page. A trace launched from a grid
        CELL cannot own its own: the cell is remounted when the grid rebuilds after
        the trace's row refresh, taking the modal with it. */}
    <TraceResultsProvider>
      <SearchResultsContent />
    </TraceResultsProvider>
  </ViewedProsProvider>
);

export default SearchResults;
